import { useState } from 'react';
import {
  getBrScoreFromAnnualAmounts,
  hasBrDebtPaymentCorrection,
  ScorecardCalculationGroupDivisionData,
  useBrNumeratorDetailsPopOverData,
} from '../../../components';
import { useBrDetails } from '../../../../BrDetails';
import { isIncomeRateCalculable } from '../../../../Scorecard';
import { useElementDetailViewIndexPage } from '../../shared';
import { Loan } from '../../../../generated/graphql';

export function useBurnRateElementDetailViewIndexPage() {
  const { householdID, tPages, tScorecard, firmID, clientID } = useElementDetailViewIndexPage();
  const [loanToEditPayments, setLoanToEditPayments] = useState<Loan | undefined>();
  const useBrNumeratorDetailsPopOverDataReturn = useBrNumeratorDetailsPopOverData({
    householdID,
    onLoansWithDebtPaymentsFootnoteLoanClick: setLoanToEditPayments,
  });

  const { brDetails } = useBrDetails({
    householdID,
    includeScore: true,
    includeAnnualIncome: true,
    includeEstimatedSpendingMinusDebtPayments: true,
  });

  const estimatedSpendingMinusDebtPayments =
    useBrNumeratorDetailsPopOverDataReturn.data.estimatedAnnualSpendingSansDebtPayments ??
    brDetails?.estimatedSpendingMinusDebtPayments.annual;
  const backendEstimatedSpendingMinusDebtPayments = brDetails?.estimatedSpendingMinusDebtPayments.annual;
  const hasDebtPaymentCorrection = hasBrDebtPaymentCorrection({
    displayAnnualSpendingSansDebtPayments: estimatedSpendingMinusDebtPayments,
    backendAnnualSpendingSansDebtPayments: backendEstimatedSpendingMinusDebtPayments,
  });

  const correctedBrScore = getBrScoreFromAnnualAmounts({
    annualSpending: estimatedSpendingMinusDebtPayments,
    annualIncome: brDetails?.annualIncome,
  });
  const isBrNotCalculable = !!brDetails && !isIncomeRateCalculable(brDetails.annualIncome);
  const brScore = isBrNotCalculable
    ? null
    : hasDebtPaymentCorrection
    ? correctedBrScore ?? brDetails?.br
    : brDetails?.br;
  const brScoreOverride = hasDebtPaymentCorrection && !isBrNotCalculable ? brScore : undefined;

  const brNumeratorData: ScorecardCalculationGroupDivisionData = {
    value: estimatedSpendingMinusDebtPayments,
    title: tScorecard('scorecard-element-calculation-group-burn-rate-numerator-title'),
  };
  const brDenominatorData: ScorecardCalculationGroupDivisionData = {
    value: brDetails?.annualIncome,
    title: tScorecard('scorecard-element-calculation-group-burn-rate-denominator-title'),
  };

  return {
    firmID,
    clientID,
    householdID,
    tPages,
    tScorecard,
    brNumeratorData,
    brDenominatorData,
    brDetails,
    brScore,
    brScoreOverride,
    loanToEditPayments,
    setLoanToEditPayments,
    useBrNumeratorDetailsPopOverDataReturn,
  };
}
